import { OrientationType } from "@package/ui-gql/index-data-access";
import type { CSSProperties } from "react";
import type { TriviaConfig } from "./trivia-types";

// Container-query units (`cqmin`) rather than viewport units: the trivia flyout is often a
// scaled, positioned sub-region of the wall (ScreenFlyout applies CSS `zoom`), not the whole
// window, so `vw`/`vh` would size text against the wrong box. `cqmin` also shrinks type on a
// short landscape box, not just a narrow one — which is what keeps controls reachable there.
// The `rem` floor means 200% browser zoom scales the minimum with the root font size while the
// container caps the maximum.
export function getTriviaTypeScaleStyle(config: TriviaConfig): CSSProperties {
  const densityScale = config.theme.density === "compact" ? 0.65 : 1;

  return {
    containerType: "size",
    containerName: "trivia",
    ["--trivia-text-meta" as string]: "clamp(0.85rem, 1.6cqmin, 1.75rem)",
    ["--trivia-text-body" as string]: "clamp(1.05rem, 2.4cqmin, 2.75rem)",
    ["--trivia-text-answer" as string]: "clamp(1.05rem, 2.6cqmin, 3rem)",
    ["--trivia-text-prompt" as string]: "clamp(1.5rem, 4.2cqmin, 5rem)",
    ["--trivia-text-display" as string]: "clamp(2rem, 7cqmin, 9rem)",
    ["--trivia-space" as string]: `clamp(${0.5 * densityScale}rem, ${1.5 * densityScale}cqmin, ${2.5 * densityScale}rem)`,
    ["--trivia-radius" as string]:
      config.theme.cornerStyle === "square"
        ? "0px"
        : "clamp(0.75rem, 2cqmin, 2rem)",
  };
}

const TRIVIA_SAFE_AREA_SIDES = ["top", "right", "bottom", "left"] as const;

// `max(base, env())` rather than a raw inset: when the flyout is a positioned sub-region
// (not at a physical screen edge), env() resolves to 0 and this degrades to the base padding;
// on kiosk hardware without a notch, it's also a no-op.
export const TRIVIA_SAFE_AREA_STYLE: CSSProperties =
  TRIVIA_SAFE_AREA_SIDES.reduce((style, side) => {
    const property = `padding${side[0].toUpperCase()}${side.slice(1)}`;
    return {
      ...style,
      [property]: `max(var(--trivia-space), env(safe-area-inset-${side}))`,
    };
  }, {} as CSSProperties);

// Shared by TriviaResultChart, TriviaResultQrCode and TriviaFeedbackChart: each scales a
// dimension (circle size, QR size, font size) off the smaller of the container's two axes,
// with a floor so it never collapses on a tiny container. Callers keep their own factor/floor.
export function getContainerScaledSize(
  containerSize: { width: number; height: number },
  factor: number,
  floor: number,
): number {
  return Math.max(
    floor,
    Math.round(Math.min(containerSize.width, containerSize.height) * factor),
  );
}

export function getAnswerGridColumns(
  count: number,
  orientation: OrientationType,
  hasMedia: boolean,
): number {
  if (count <= 0) {
    return 1;
  }

  let columns: number;

  if (hasMedia) {
    columns =
      orientation === OrientationType.Landscape ? Math.min(count, 4) : 2;
  } else if (orientation === OrientationType.Landscape) {
    if (count <= 2) {
      columns = 2;
    } else if (count === 3) {
      columns = 3;
    } else if (count === 4) {
      columns = 2;
    } else {
      columns = 3;
    }
  } else {
    columns = count <= 2 ? 1 : 2;
  }

  return Math.max(1, Math.min(columns, count));
}
